import React, { useLayoutEffect, useCallback } from "react";
import Navbar from "./Navbar";
import Home from "./container/Home";
import Services from "./container/Services";
import Portfolio from "./container/Portfolio";
import ToolsAndExperiences from "./container/ToolsAndExperiences";
import Connect from "./container/Connect";
import ScrollBar from "./ScrollBar";
import Menu from "./Menu";
import Backdrop from "./Backdrop";
import Preloader from "./Preloader";
import ProjectModal from "./components/ProjectModal";
import TradingViewModal from "./components/TradingViewModal";
import SpotifyPlayer from "./components/SpotifyPlayer";
import projectsData from "./data/projects.json";
import playlistsData from "./data/playlists.json";
import "./index.scss";
import Orb from "./container/Orb";
import TargetCursor from "./TargetCursor";

export default function App() {
  const [uiSettings, setUiSettings] = React.useState({
    showMenu: false,
    showPreloader: true,
    showing: "preloader",
    isFrench: false,
  });

  const [projectModal, setProjectModal] = React.useState({
    isOpen: false,
    project: null,
  });

  const [tradingViewModal, setTradingViewModal] = React.useState({
    isOpen: false,
    item: null,
  });

  const [spotifyModal, setSpotifyModal] = React.useState({
    isOpen: false,
    playlist: null,
  });

  function toggleLanguage() {
    setUiSettings((prevState) => {
      return { ...prevState, showMenu: false, isFrench: !prevState.isFrench };
    });
  }

  const toggleMenu = useCallback(() => {
    setUiSettings((prevState) => {
      return { ...prevState, showMenu: !prevState.showMenu };
    });
  }, []);

  // Helper function to update URL with section
  function updateUrlSection(section) {
    const validSections = [
      "home",
      "services",
      "portfolio",
      "experience",
      "contact",
    ];
    if (validSections.includes(section)) {
      const path = section === "home" ? "/" : `/${section}`;
      window.history.pushState({}, "", path);
    }
  }

  // Helper function to get section from URL
  function getSectionFromUrl() {
    const pathname = window.location.pathname;
    const validSections = [
      "home",
      "services",
      "portfolio",
      "experience",
      "contact",
    ];

    // Remove leading slash and get the section
    const section = pathname === "/" ? "home" : pathname.slice(1);

    return validSections.includes(section) ? section : "home";
  }

  const gotoHome = useCallback(() => {
    updateUrlSection("home");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
        showing: "home",
      };
    });
  }, []);

  const gotoServices = useCallback(() => {
    updateUrlSection("services");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
        showing: "services",
      };
    });
  }, []);

  const gotoPortfolio = useCallback(() => {
    updateUrlSection("portfolio");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
        showing: "portfolio",
      };
    });
  }, []);

  const gotoExperience = useCallback(() => {
    updateUrlSection("experience");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
        showing: "experience",
      };
    });
  }, []);

  const gotoContact = useCallback(() => {
    updateUrlSection("contact");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showing: "contact",
        showMenu: false,
      };
    });
  }, []);

  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;
  const transitionDuration = isMobile ? 0 : 400;

  function gotoHomeFromMenu() {
    updateUrlSection("home");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
      };
    });

    setTimeout(() => {
      setUiSettings((prevState) => {
        return {
          ...prevState,
          showing: "home",
        };
      });
    }, transitionDuration);
  }

  function gotoServicesFromMenu() {
    updateUrlSection("services");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
      };
    });

    setTimeout(() => {
      setUiSettings((prevState) => {
        return {
          ...prevState,
          showing: "services",
        };
      });
    }, transitionDuration);
  }

  function gotoPortfolioFromMenu() {
    updateUrlSection("portfolio");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
      };
    });

    setTimeout(() => {
      setUiSettings((prevState) => {
        return {
          ...prevState,
          showing: "portfolio",
        };
      });
    }, transitionDuration);
  }

  function gotoExperienceFromMenu() {
    updateUrlSection("experience");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
      };
    });

    setTimeout(() => {
      setUiSettings((prevState) => {
        return {
          ...prevState,
          showing: "experience",
        };
      });
    }, transitionDuration);
  }

  function gotoContactFromMenu() {
    updateUrlSection("contact");
    setUiSettings((prevState) => {
      return {
        ...prevState,
        showMenu: false,
      };
    });

    setTimeout(() => {
      setUiSettings((prevState) => {
        return {
          ...prevState,
          showing: "contact",
        };
      });
    }, transitionDuration);
  }

  const closeMenu = useCallback(() => {
    setUiSettings((prevState) => {
      return { ...prevState, showMenu: false };
    });
  }, []);

  // Helper function to create project slug from name
  const createProjectSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }, []);

  // Helper function to find project by slug
  const findProjectBySlug = useCallback(
    (slug) => {
      return projectsData.find(
        (project) => createProjectSlug(project.name) === slug,
      );
    },
    [createProjectSlug],
  );

  // Helper function to update URL with project
  const updateUrlProject = useCallback(
    (project) => {
      if (project) {
        const slug = createProjectSlug(project.name);
        const url = new URL(window.location);
        url.searchParams.set("project", slug);
        window.history.pushState({}, "", url);
      }
    },
    [createProjectSlug],
  );

  // Helper function to remove project from URL
  const removeUrlProject = useCallback(() => {
    const url = new URL(window.location);
    url.searchParams.delete("project");
    window.history.pushState({}, "", url);
  }, []);

  // Helper function to get project from URL
  const getProjectFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const projectSlug = urlParams.get("project");
    if (projectSlug) {
      return findProjectBySlug(projectSlug);
    }
    return null;
  }, [findProjectBySlug]);

  const openProjectModal = useCallback(
    (project) => {
      updateUrlProject(project);
      setProjectModal({ isOpen: true, project });
    },
    [updateUrlProject],
  );

  const closeProjectModal = useCallback(() => {
    removeUrlProject();
    setProjectModal({ isOpen: false, project: null });
  }, [removeUrlProject]);

  const refreshProjectModal = useCallback(() => {
    // The modal component handles the refresh internally via key change
  }, []);

  // Helper function to create asset slug from symbol
  const createAssetSlug = useCallback((symbol) => {
    return symbol.toLowerCase().replace(/[^a-z0-9]/g, "");
  }, []);

  // Helper function to find asset by symbol
  const findAssetBySymbol = useCallback((symbol) => {
    // Import asset lists (these match Top10Crypto component)
    const top10Cryptos = [
      { id: "bitcoin", symbol: "BTC", name: "Bitcoin" },
      { id: "ethereum", symbol: "ETH", name: "Ethereum" },
      { id: "tether", symbol: "USDT", name: "Tether" },
      { id: "binancecoin", symbol: "BNB", name: "BNB" },
      { id: "solana", symbol: "SOL", name: "Solana" },
      { id: "the-open-network", symbol: "TON", name: "Toncoin" },
      { id: "dogecoin", symbol: "DOGE", name: "Dogecoin" },
      { id: "cardano", symbol: "ADA", name: "Cardano" },
      { id: "midnight-3", symbol: "NIGHT", name: "Midnight" },
      { id: "usd-coin", symbol: "USDC", name: "USD Coin" },
      { id: "tron", symbol: "TRX", name: "TRON" },
      { id: "chainlink", symbol: "LINK", name: "Chainlink" },
      { id: "hyperliquid", symbol: "HYPE", name: "Hyperliquid" },
      { id: "stellar", symbol: "XLM", name: "Stellar" },
      { id: "sui", symbol: "SUI", name: "Sui" },
      { id: "bitcoin-cash", symbol: "BCH", name: "Bitcoin Cash" },
      { id: "hedera-hashgraph", symbol: "HBAR", name: "Hedera" },
      { id: "ethena-usde", symbol: "USDe", name: "Ethena USDe" },
      { id: "avalanche-2", symbol: "AVAX", name: "Avalanche" },
      { id: "litecoin", symbol: "LTC", name: "Litecoin" },
      { id: "ripple", symbol: "XRP", name: "XRP" },
    ];

    const top3Metals = [
      { id: "pax-gold", symbol: "XAU", name: "Gold" },
      {
        id: "ishares-silver-trust-ondo-tokenized-stock",
        symbol: "XAG",
        name: "Silver",
      },
      { id: "diamond-standard-carat", symbol: "XDI", name: "Diamonds" },
    ];

    const top10Stocks = [
      {
        id: "microsoft-ondo-tokenized-stock",
        symbol: "MSFT",
        name: "Microsoft",
      },
      { id: "apple-ondo-tokenized-stock", symbol: "AAPL", name: "Apple" },
      { id: "nvidia-ondo-tokenized-stock", symbol: "NVDA", name: "NVIDIA" },
      {
        id: "alphabet-class-a-ondo-tokenized-stock",
        symbol: "GOOGL",
        name: "Alphabet",
      },
      { id: "amazon-ondo-tokenized-stock", symbol: "AMZN", name: "Amazon" },
      {
        id: "meta-platforms-ondo-tokenized-stock",
        symbol: "META",
        name: "Meta",
      },
      { id: "tesla-ondo-tokenized-stock", symbol: "TSLA", name: "Tesla" },
      {
        id: "jpmorgan-chase-ondo-tokenized-stock",
        symbol: "JPM",
        name: "JPMorgan Chase",
      },
      { id: "broadcom-ondo-tokenized-stock", symbol: "AVGO", name: "Broadcom" },
      { id: "visa-ondo-tokenized-stock", symbol: "V", name: "Visa" },
    ];

    const allAssets = [...top10Cryptos, ...top3Metals, ...top10Stocks];
    const normalizedSymbol = symbol.toUpperCase();
    return allAssets.find(
      (asset) => asset.symbol.toUpperCase() === normalizedSymbol,
    );
  }, []);

  // Helper function to update URL with asset
  const updateUrlAsset = useCallback(
    (item) => {
      if (item) {
        const slug = createAssetSlug(item.symbol);
        const url = new URL(window.location);
        url.searchParams.set("asset", slug);
        window.history.pushState({}, "", url);
      }
    },
    [createAssetSlug],
  );

  // Helper function to remove asset from URL
  const removeUrlAsset = useCallback(() => {
    const url = new URL(window.location);
    url.searchParams.delete("asset");
    window.history.pushState({}, "", url);
  }, []);

  // Helper function to get asset from URL
  const getAssetFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const assetSlug = urlParams.get("asset");
    if (assetSlug) {
      // Convert slug back to symbol (uppercase)
      const symbol = assetSlug.toUpperCase();
      return findAssetBySymbol(symbol);
    }
    return null;
  }, [findAssetBySymbol]);

  const openTradingViewModal = useCallback(
    (item) => {
      updateUrlAsset(item);
      setTradingViewModal({ isOpen: true, item });
    },
    [updateUrlAsset],
  );

  const closeTradingViewModal = useCallback(() => {
    removeUrlAsset();
    setTradingViewModal({ isOpen: false, item: null });
  }, [removeUrlAsset]);

  // Helper function to create playlist slug from name
  const createPlaylistSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/\[|\]/g, "");
  }, []);

  // Helper function to find playlist by slug (uses playlists.json)
  const findPlaylistBySlug = useCallback(
    (slug) => {
      const playlists = playlistsData.playlists.filter(
        (p) => !p.platform || p.platform === "spotify",
      );
      return playlists.find(
        (playlist) => createPlaylistSlug(playlist.name) === slug,
      );
    },
    [createPlaylistSlug],
  );

  // Helper function to update URL with Spotify modal and playlist
  const updateUrlSpotify = useCallback(
    (playlist) => {
      if (playlist) {
        const slug = createPlaylistSlug(playlist.name);
        const url = new URL(window.location);
        url.searchParams.set("spotify", slug);
        window.history.pushState({}, "", url);
      } else {
        const url = new URL(window.location);
        url.searchParams.set("spotify", "playlist");
        window.history.pushState({}, "", url);
      }
    },
    [createPlaylistSlug],
  );

  // Helper function to remove Spotify from URL
  const removeUrlSpotify = useCallback(() => {
    const url = new URL(window.location);
    url.searchParams.delete("spotify");
    window.history.pushState({}, "", url);
  }, []);

  // Helper function to get Spotify playlist from URL
  const getSpotifyFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const spotifyParam = urlParams.get("spotify");
    if (spotifyParam && spotifyParam !== "playlist") {
      return findPlaylistBySlug(spotifyParam);
    }
    return spotifyParam === "playlist" ? { isDefault: true } : null;
  }, [findPlaylistBySlug]);

  const openSpotifyModal = useCallback(
    (playlist) => {
      updateUrlSpotify(playlist);
      setSpotifyModal({ isOpen: true, playlist });
    },
    [updateUrlSpotify],
  );

  const closeSpotifyModal = useCallback(() => {
    removeUrlSpotify();
    setSpotifyModal({ isOpen: false, playlist: null });
  }, [removeUrlSpotify]);

  React.useEffect(function () {
    const hidePreloader = localStorage.getItem("hidePreloader");
    const initialSection = getSectionFromUrl();

    if (!hidePreloader) {
      const timeDuration = initialSection === "home" ? 1500 : 100;
      setTimeout(function () {
        setUiSettings((prevState) => {
          return {
            ...prevState,
            showing: initialSection,
          };
        });
      }, timeDuration);
      setTimeout(function () {
        localStorage.setItem("hidePreloader", true);
        setUiSettings((prevState) => {
          return {
            ...prevState,
            showPreloader: false,
          };
        });
      }, 1500);
    } else {
      const timeDuration = initialSection === "home" ? 700 : 100;
      setTimeout(function () {
        setUiSettings((prevState) => {
          return {
            ...prevState,
            showing: initialSection,
          };
        });
      }, timeDuration);
      setTimeout(function () {
        setUiSettings((prevState) => {
          return {
            ...prevState,
            showPreloader: false,
          };
        });
      }, 700);
    }
  }, []);

  // Handle browser back/forward navigation
  React.useEffect(
    function () {
      function handlePopState() {
        const section = getSectionFromUrl();
        setUiSettings((prevState) => {
          return { ...prevState, showing: section, showMenu: false };
        });

        // Check for project in URL
        const projectFromUrl = getProjectFromUrl();
        if (projectFromUrl) {
          setProjectModal({ isOpen: true, project: projectFromUrl });
        } else {
          // Project not found, clear from URL and close modal
          removeUrlProject();
          setProjectModal({ isOpen: false, project: null });
        }

        // Check for asset in URL
        const assetFromUrl = getAssetFromUrl();
        if (assetFromUrl) {
          setTradingViewModal({ isOpen: true, item: assetFromUrl });
        } else {
          // Asset not found, clear from URL and close modal
          removeUrlAsset();
          setTradingViewModal({ isOpen: false, item: null });
        }

        // Check for Spotify modal in URL
        const spotifyFromUrl = getSpotifyFromUrl();
        if (spotifyFromUrl) {
          setSpotifyModal({
            isOpen: true,
            playlist: spotifyFromUrl.isDefault ? null : spotifyFromUrl,
          });
        } else {
          // Spotify not in URL, close modal
          removeUrlSpotify();
          setSpotifyModal({ isOpen: false, playlist: null });
        }
      }

      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    },
    [
      getProjectFromUrl,
      removeUrlProject,
      getAssetFromUrl,
      removeUrlAsset,
      getSpotifyFromUrl,
      removeUrlSpotify,
      findPlaylistBySlug,
    ],
  );

  // Initialize project modal from URL on mount or when portfolio section is shown
  React.useEffect(
    function () {
      // Only check for project in URL when on portfolio section
      if (uiSettings.showing === "portfolio") {
        const urlParams = new URLSearchParams(window.location.search);
        const projectSlug = urlParams.get("project");

        if (projectSlug) {
          const projectFromUrl = getProjectFromUrl();
          if (projectFromUrl) {
            // Check if it's a valid project (not Google Drive, GitHub, or Figma)
            const isGoogleDrive =
              projectFromUrl.livesite?.includes("drive.google.com");
            const isGitHub = projectFromUrl.livesite?.includes("github.com");
            const isFigma = projectFromUrl.livesite?.includes("figma.com");
            if (!isGoogleDrive && !isGitHub && !isFigma) {
              // Only open if modal is not already open with this project
              if (
                !projectModal.isOpen ||
                projectModal.project?.name !== projectFromUrl.name
              ) {
                setProjectModal({ isOpen: true, project: projectFromUrl });
              }
            } else {
              // Invalid project type, clear from URL
              removeUrlProject();
            }
          } else {
            // Project not found, clear from URL
            removeUrlProject();
          }
        } else if (projectModal.isOpen) {
          // If no project in URL but modal is open, close it
          setProjectModal({ isOpen: false, project: null });
        }
      }
    },
    [
      uiSettings.showing,
      getProjectFromUrl,
      removeUrlProject,
      projectModal.isOpen,
      projectModal.project?.name,
    ],
  );

  // Initialize TradingView modal from URL on mount or when home section is shown
  React.useEffect(
    function () {
      // Only check for asset in URL when on home section
      if (uiSettings.showing === "home") {
        const urlParams = new URLSearchParams(window.location.search);
        const assetSlug = urlParams.get("asset");

        if (assetSlug) {
          const assetFromUrl = getAssetFromUrl();
          if (assetFromUrl) {
            // Only open if modal is not already open with this asset
            if (
              !tradingViewModal.isOpen ||
              tradingViewModal.item?.symbol !== assetFromUrl.symbol
            ) {
              setTradingViewModal({ isOpen: true, item: assetFromUrl });
            }
          } else {
            // Asset not found, clear from URL
            removeUrlAsset();
          }
        } else if (tradingViewModal.isOpen) {
          // If no asset in URL but modal is open, close it
          setTradingViewModal({ isOpen: false, item: null });
        }
      }
    },
    [
      uiSettings.showing,
      getAssetFromUrl,
      removeUrlAsset,
      tradingViewModal.isOpen,
      tradingViewModal.item?.symbol,
    ],
  );

  // Initialize Spotify modal from URL on mount or when section changes
  React.useEffect(
    function () {
      const spotifyFromUrl = getSpotifyFromUrl();

      if (spotifyFromUrl) {
        // Only open if modal is not already open with this playlist
        const playlistName = spotifyFromUrl.isDefault
          ? null
          : spotifyFromUrl.name;
        if (
          !spotifyModal.isOpen ||
          spotifyModal.playlist?.name !== playlistName
        ) {
          setSpotifyModal({
            isOpen: true,
            playlist: spotifyFromUrl.isDefault ? null : spotifyFromUrl,
          });
        }
      } else if (spotifyModal.isOpen) {
        // If no Spotify in URL but modal is open, close it
        setSpotifyModal({ isOpen: false, playlist: null });
      }
    },
    [
      uiSettings.showing,
      getSpotifyFromUrl,
      spotifyModal.isOpen,
      spotifyModal.playlist?.name,
    ],
  );

  React.useEffect(
    function () {
      function handleKeyDown(event) {
        // Handle Escape key first (works even when modal is open)
        if (event.key === "Escape") {
          event.preventDefault();
          if (projectModal.isOpen) {
            closeProjectModal();
          } else if (uiSettings.showMenu) {
            closeMenu();
          }
          return;
        }

        // Ignore if modal is open, preloader is showing, or user is typing in an input/textarea
        if (
          projectModal.isOpen ||
          uiSettings.showPreloader ||
          event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.isContentEditable
        ) {
          return;
        }

        // Handle menu toggle keys
        if (event.key === "m" || event.key === "M") {
          // Disable M key on screens wider than 1200px
          if (screenWidth > 1200) {
            return;
          }
          event.preventDefault();
          toggleMenu();
          return;
        }

        // Handle spider toggle
        if (event.key === "x" || event.key === "X") {
          event.preventDefault();
          const spiderElement = document.querySelector(".spider");
          if (spiderElement) {
            spiderElement.click();
          }
          return;
        }

        // Handle portfolio project list scrolling with Cmd/Ctrl + Up/Down
        if (
          (event.metaKey || event.ctrlKey) &&
          uiSettings.showing === "portfolio"
        ) {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            const projectsContainer = document.getElementById("projects");
            if (!projectsContainer) return;

            const scrollStep = 250;
            const startPosition = projectsContainer.scrollTop;
            let targetPosition;

            if (event.key === "ArrowUp") {
              targetPosition = Math.max(0, startPosition - scrollStep);
            } else {
              const maxScroll =
                projectsContainer.scrollHeight - projectsContainer.clientHeight;
              targetPosition = Math.min(maxScroll, startPosition + scrollStep);
            }

            const distance = targetPosition - startPosition;
            const duration = 300;
            let startTime = null;

            const animateScroll = (currentTime) => {
              if (startTime === null) startTime = currentTime;
              const timeElapsed = currentTime - startTime;
              const progress = Math.min(timeElapsed / duration, 1);
              const ease = 1 - Math.pow(1 - progress, 3);
              projectsContainer.scrollTop = startPosition + distance * ease;
              if (progress < 1) {
                requestAnimationFrame(animateScroll);
              }
            };

            requestAnimationFrame(animateScroll);
            return;
          }
        }

        // Handle portfolio filter switching with Cmd/Ctrl + Left/Right
        if (
          (event.metaKey || event.ctrlKey) &&
          uiSettings.showing === "portfolio"
        ) {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            const filterBar = document.querySelector(".filter-bar");
            if (!filterBar) return;

            const filters = Array.from(filterBar.querySelectorAll(".filter"));
            const activeIndex = filters.findIndex((filter) =>
              filter.classList.contains("active"),
            );

            if (activeIndex === -1) return;

            let targetIndex;
            if (event.key === "ArrowRight") {
              // Move to next filter, wrap around to first if at end
              targetIndex = (activeIndex + 1) % filters.length;
            } else {
              // Move to previous filter, wrap around to last if at start
              targetIndex =
                activeIndex === 0 ? filters.length - 1 : activeIndex - 1;
            }

            filters[targetIndex]?.click();
            return;
          }
        }

        // Handle experience list scrolling with Cmd/Ctrl + Up/Down
        if (
          (event.metaKey || event.ctrlKey) &&
          uiSettings.showing === "experience"
        ) {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            const experienceContainer =
              document.querySelector(".experience .xp");
            if (!experienceContainer) return;

            const scrollStep = 250;
            const startPosition = experienceContainer.scrollTop;
            let targetPosition;

            if (event.key === "ArrowUp") {
              targetPosition = Math.max(0, startPosition - scrollStep);
            } else {
              const maxScroll =
                experienceContainer.scrollHeight -
                experienceContainer.clientHeight;
              targetPosition = Math.min(maxScroll, startPosition + scrollStep);
            }

            const distance = targetPosition - startPosition;
            const duration = 300;
            let startTime = null;

            const animateScroll = (currentTime) => {
              if (startTime === null) startTime = currentTime;
              const timeElapsed = currentTime - startTime;
              const progress = Math.min(timeElapsed / duration, 1);
              const ease = 1 - Math.pow(1 - progress, 3);
              experienceContainer.scrollTop = startPosition + distance * ease;
              if (progress < 1) {
                requestAnimationFrame(animateScroll);
              }
            };

            requestAnimationFrame(animateScroll);
            return;
          }
        }

        // Ignore arrow keys when menu is open
        if (uiSettings.showMenu) {
          return;
        }

        const sections = [
          "home",
          "services",
          "portfolio",
          "experience",
          "contact",
        ];
        const currentIndex = sections.indexOf(uiSettings.showing);

        if (event.key === "ArrowDown" && currentIndex < sections.length - 1) {
          event.preventDefault();
          const nextSection = sections[currentIndex + 1];
          if (nextSection === "home") gotoHome();
          else if (nextSection === "services") gotoServices();
          else if (nextSection === "portfolio") gotoPortfolio();
          else if (nextSection === "experience") gotoExperience();
          else if (nextSection === "contact") gotoContact();
        } else if (event.key === "ArrowUp" && currentIndex > 0) {
          event.preventDefault();
          const prevSection = sections[currentIndex - 1];
          if (prevSection === "home") gotoHome();
          else if (prevSection === "services") gotoServices();
          else if (prevSection === "portfolio") gotoPortfolio();
          else if (prevSection === "experience") gotoExperience();
          else if (prevSection === "contact") gotoContact();
        }
      }

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    },
    [
      uiSettings.showing,
      uiSettings.showMenu,
      uiSettings.showPreloader,
      screenWidth,
      projectModal.isOpen,
      gotoHome,
      gotoServices,
      gotoPortfolio,
      gotoExperience,
      gotoContact,
      closeProjectModal,
      toggleMenu,
      closeMenu,
    ],
  );

  useLayoutEffect(() => {
    const showing = uiSettings?.showing;
    const showMenu = uiSettings?.showMenu;
    function updateThemeColor(color) {
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');

      if (themeColorMeta) {
        themeColorMeta.setAttribute("content", color);
      } else {
        themeColorMeta = document.createElement("meta");
        themeColorMeta.setAttribute("name", "theme-color");
        themeColorMeta.setAttribute("content", color);

        document.getElementsByTagName("head")[0].appendChild(themeColorMeta);
      }
    }

    const updateThemeBasedOnState = () => {
      // Update theme color based on modal theme when any modal is open
      if (
        tradingViewModal.isOpen ||
        spotifyModal.isOpen ||
        projectModal.isOpen
      ) {
        const modalTheme = localStorage.getItem("ModalTheme") || "light";
        const themeColor = modalTheme === "light" ? "#FFFFFF" : "#000000";
        updateThemeColor(themeColor);
        return;
      }

      // Update theme color to black when project modal is open on mobile
      if (projectModal.isOpen && isMobile) {
        updateThemeColor("#000000");
        return;
      }

      if (showing === "home") {
        updateThemeColor("#FFFFFF");
      } else if (showing === "services") {
        updateThemeColor("#FFFFFF");
      } else if (showing === "portfolio") {
        updateThemeColor("#202020");
      } else if (showing === "experience") {
        updateThemeColor("#a0a0a0");
        if (showMenu) {
          updateThemeColor("#202020");
        }
      } else if (showing === "contact") {
        updateThemeColor("#202020");
        if (showMenu) {
          updateThemeColor("#202020");
        }
      } else if (uiSettings?.showPreloader) {
        updateThemeColor("#FFFFFF");
      } else {
        updateThemeColor("#FFFFFF");
        if (showMenu) {
          updateThemeColor("#FFFFFF");
        }
      }
    };

    updateThemeBasedOnState();

    // Listen for storage changes to update theme when modal theme changes
    const handleStorageChange = (e) => {
      if (e.key === "ModalTheme") {
        updateThemeBasedOnState();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for custom event when any modal theme changes
    const handleModalThemeChange = () => {
      updateThemeBasedOnState();
    };

    window.addEventListener("modalThemeChange", handleModalThemeChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("modalThemeChange", handleModalThemeChange);
    };
  }, [
    uiSettings.showing,
    uiSettings.showMenu,
    uiSettings?.showPreloader,
    projectModal.isOpen,
    spotifyModal.isOpen,
    tradingViewModal.isOpen,
    isMobile,
  ]);

  return (
    <div
      className={`container showing-${uiSettings.showing} ${
        uiSettings.showMenu ? "show-menu" : ""
      }`}
      onClick={() => closeMenu()}
    >
      <div className="orb-holder">
        <Orb
          hoverIntensity={0.5}
          rotateOnHover={false}
          hue={0}
          forceHoverState={uiSettings.showing !== "home"}
        />
      </div>
      {!uiSettings.showPreloader && <TargetCursor />}
      <Navbar
        className="navbar"
        toggleMenu={toggleMenu}
        showMenu={uiSettings.showMenu}
        gotoHome={gotoHome}
        gotoServices={gotoServices}
        gotoPortfolio={gotoPortfolio}
        gotoExperience={gotoExperience}
        gotoContact={gotoContact}
        showing={uiSettings.showing}
        toggleLanguage={toggleLanguage}
        isFrench={uiSettings.isFrench}
      />
      <ScrollBar
        className={uiSettings.showMenu ? "scrollbar show" : "scrollbar"}
        gotoHome={gotoHome}
        gotoServices={gotoServices}
        gotoPortfolio={gotoPortfolio}
        gotoExperience={gotoExperience}
        gotoContact={gotoContact}
        showing={uiSettings.showing}
      />
      <Home
        className="home section"
        swipeUp={gotoContact}
        swipeDown={gotoServices}
        isFrench={uiSettings.isFrench}
        scrollToPortfolio={gotoPortfolio}
        openTradingViewModal={openTradingViewModal}
      />
      <Services
        className="services section"
        swipeUp={gotoHome}
        gotoPortfolio={gotoPortfolio}
        gotoContact={gotoContact}
        gotoHome={gotoHome}
        isFrench={uiSettings.isFrench}
      />
      <Portfolio
        className="portfolio section"
        swipeUp={gotoServices}
        swipeDown={gotoExperience}
        isFrench={uiSettings.isFrench}
        openProjectModal={openProjectModal}
      />
      <ToolsAndExperiences
        className="experience section"
        swipeUp={gotoPortfolio}
        swipeDown={gotoContact}
        isFrench={uiSettings.isFrench}
      />
      <Connect
        className="contact section"
        swipeUp={gotoExperience}
        swipeDown={gotoHome}
        isFrench={uiSettings.isFrench}
      />
      <Menu
        className={uiSettings.showMenu ? "show menu" : "menu"}
        gotoHome={gotoHomeFromMenu}
        gotoServices={gotoServicesFromMenu}
        gotoPortfolio={gotoPortfolioFromMenu}
        gotoExperience={gotoExperienceFromMenu}
        gotoContact={gotoContactFromMenu}
        showing={uiSettings.showing}
        toggleLanguage={toggleLanguage}
        isFrench={uiSettings.isFrench}
      />
      <Backdrop
        className={uiSettings.showMenu ? "show backdrop" : "backdrop"}
      />
      <Preloader
        className={uiSettings.showPreloader ? "preloader" : "preloader hide"}
      />
      <ProjectModal
        project={projectModal.project}
        isOpen={projectModal.isOpen}
        onClose={closeProjectModal}
        onRefresh={refreshProjectModal}
      />
      <TradingViewModal
        item={tradingViewModal.item}
        isOpen={tradingViewModal.isOpen}
        onClose={closeTradingViewModal}
      />
      <SpotifyPlayer
        isModalOpen={spotifyModal.isOpen}
        selectedPlaylist={spotifyModal.playlist}
        onOpenModal={openSpotifyModal}
        onCloseModal={closeSpotifyModal}
      />
    </div>
  );
}
